import { IGbIndex } from '@/interfaces/benchmarks/index/IIndex.ts';

export enum LeaderboardPoolingModeEnum {
    NonPooled = 'nonPooled',
    AssetPool = 'assetPool',
}

/** Omitting scope selects the whole pool. */
export enum BenchmarkPoolScopeTypeEnum {
    Pool = 'Pool',
    Category = 'Category',
}

/** Enact accepts enum names on writes and serializes enum values as numbers. */
export interface IBenchmarkPoolAssetFilter {
    selectedPlants?: Array<string>;
    selectedFuels?: Array<string | number>;
    selectedZones?: Array<string | number | null>;
    selectedBmStatuses?: Array<string | number | null>;
    selectedOwners?: Array<string>;
    selectedOptimisers?: Array<string>;
    minimumCapacityMw?: number | null;
    maximumCapacityMw?: number | null;
    minimumDurationHours?: number | null;
    maximumDurationHours?: number | null;
    includeColocation?: boolean;
    restrictToSelectedPlants?: boolean;
}

export interface IBenchmarkPoolCategoryDefinition {
    id: string;
    name?: string | null;
    priority: number;
    assetFilter: IBenchmarkPoolAssetFilter;
}

export interface IBenchmarkPoolDefinitionInput {
    name: string;
    benchmarkPlantId: string;
    requiredPoolSize: number;
    categories: Array<IBenchmarkPoolCategoryDefinition>;
}

export interface IBenchmarkPoolDefinition extends IBenchmarkPoolDefinitionInput {
    id: string;
}

export interface IBenchmarkPoolPreviewRequest {
    poolId: string;
    /** Inclusive calendar dates, YYYY-MM-DD. */
    from: string;
    to: string;
    approveUndersizedPool?: boolean;
}

export interface IResolvedBenchmarkPoolAsset {
    plantId: string;
    categoryId: string;
    categoryName: string | null;
    categoryPriority: number;
    distanceKm: number | null;
}

export interface IResolvedBenchmarkPoolCategory {
    categoryId: string;
    categoryName: string | null;
    categoryPriority: number;
    assets: Array<IResolvedBenchmarkPoolAsset>;
}

export interface IBenchmarkPoolExclusion {
    plantId: string;
    categoryId: string;
    reason: string;
}

export interface IResolvedBenchmarkPool {
    poolId: string;
    poolName: string | null;
    revisionId: string;
    benchmarkPlantId: string;
    from: string;
    to: string;
    requiredPoolSize: number;
    requiresUndersizedApproval: boolean;
    isUndersizedApproved: boolean;
    assets: Array<IResolvedBenchmarkPoolAsset>;
    categories: Array<IResolvedBenchmarkPoolCategory>;
    historicalExclusions: Array<IBenchmarkPoolExclusion>;
    excludedPlantIdsWithoutCoordinates: Array<string>;
}

export interface IBenchmarkPoolScope {
    type?: BenchmarkPoolScopeTypeEnum;
    categoryId?: string;
}

export interface ILeaderboardBenchmarkPoolCategory {
    id: string;
    name?: string;
    indexFilters: IGbIndex;
    /** Preserve backend conditions the index editor cannot represent. */
    assetFilter?: IBenchmarkPoolAssetFilter;
    priority?: number;
}

export interface ILeaderboardBenchmarkPoolConfig {
    benchmarkPlantId: string;
    requiredPoolSize: number;
    categories: Array<ILeaderboardBenchmarkPoolCategory>;
}

/** Editor representation of a persisted definition. */
export interface IBenchmarkPoolRecord {
    id: string;
    name: string;
    config: ILeaderboardBenchmarkPoolConfig;
}

export interface ILeaderboardBenchmarkPoolReference {
    poolId: string;
    revisionId: string;
}

/** Binds an explicit approval to the previewed saved content, period and signed-in session. */
export interface IBenchmarkPoolApprovalContext {
    sessionId: string;
    configFingerprint: string;
    from: string;
    to: string;
}
